import i18next from 'i18next';

import { AppName } from './Constants';
import { UrlVars } from './ReactScriptHelper';
import { captureException } from './Error';
import { sentrySyncTransportLog } from './errors/Transport';

import { DialogBaseButton } from '@egr/xbox/egr-gui-dialogs/DialogBase';
import { sendMailInBrowser } from '@egr/xbox/share-by-mail/MailUtils';
import { versionLabelInApp } from '@egr/xbox/app-api/AppInfo';

import { isNotNullOrEmpty } from '@egr/wcf/modules/utils/string';

type SendMailCallback = (bodyOfMail: string, email: string, subject: string) => void;

export interface ErrorInformation {
    errorId: string | undefined;
    error: Error;
}

function sendErrorMail(
    email: string,
    errorInfo: ErrorInformation,
    product: string,
    sendCallback: SendMailCallback = sendMailInBrowser,
    appUrl?: string
): void {
    const validErrorId = errorInfo.errorId != null && sentrySyncTransportLog.get(errorInfo.error) === true;
    const appVersion: string = versionLabelInApp();
    const userAgent: string = navigator.userAgent.toLocaleLowerCase();
    const subject: string = `[ERROR] ${product} ${appVersion}`;
    const body: string = [
        ...(
            validErrorId ? [
                i18next.t('Error ID: {{errorId}}', {errorId: errorInfo.errorId}),
                i18next.t('Please describe the problem as detailed as possible. Then send this mail with the above mentioned error ID to {{email}}.', {email}),
            ] : [
                i18next.t('Please describe the problem as detailed as possible. Then send this mail to {{email}}.', {email}),
                i18next.t('Error message: {{errorMessage}}', {errorMessage: errorInfo.error.message})
            ]
        ),
        `User Agent: ${userAgent}`,
        isNotNullOrEmpty(appUrl) ? `URL: ${appUrl}` : null
    ].filter(isNotNullOrEmpty).join('\n\n');

    sendCallback(body, email, subject);
}

function sendDataErrorMail(
    email: string,
    manufacturer: string | undefined,
    articlecode: string | undefined,
    body: string,
    message?: string | undefined,
    sendCallback: SendMailCallback = sendMailInBrowser
): void {
    sendCallback(body, email, `[DATA-ERROR][${AppName ?? ''}] ${manufacturer ?? ''} - ${articlecode ?? ''} - ${message ?? ''}`);
}

export function getFeedbackButton(
    email: string,
    errorInfo: ErrorInformation,
    product: string,
    sendCallback: SendMailCallback = sendMailInBrowser,
    appUrl?: string
): DialogBaseButton | undefined {
    if (isNotNullOrEmpty(errorInfo.errorId) && isNotNullOrEmpty(email)) {
        return {
            label: i18next.t('Report issue'),
            key: 'report-issue',
            preventCloseCall: true,
            callback: async (): Promise<void> => {
                try {
                    sendErrorMail(email, errorInfo, product, sendCallback, appUrl);
                } catch (error) {
                    captureException(error, 'debug');
                }
            }
        };
    }
    return undefined;
}

export function getDataErrorFeedbackButton(
    email: string,
    error: Error,
    manufacturer: string | undefined,
    articleNumber: string | undefined,
    message: string,
    isPropertyAction?: boolean,
    sendCallback: SendMailCallback = sendMailInBrowser
): DialogBaseButton | undefined {
    if (isNotNullOrEmpty(email)) {
        return {
            label: i18next.t('Report issue'),
            key: 'email-data-error',
            preventCloseCall: true,
            callback: () => {
                sendDataErrorMail(
                    email,
                    manufacturer,
                    articleNumber,
                    message + '\n\n' + error.message + '\n\n' +
                    (UrlVars.eaiws_server === undefined ? 'Live-Server' : UrlVars.eaiws_server),
                    isPropertyAction ? message : error.message.split('\n')[0],
                    sendCallback
                );
            }
        };
    }

    return undefined;
}
